/**
 * PROVISIONAL API SURFACE — nothing in this file is in `@genex/contracts` yet.
 *
 * Every type and every path here is a shape the console has been **built against** but which
 * the contract does not publish. Each one is listed in the work order's report as a required
 * contract change, with the exact route, method, request body and response body copied out of
 * here. The moment the real thing lands in `packages/contracts`, the block below must be
 * **deleted** and re-imported from `@genex/contracts` through `./types.js`, exactly like every
 * other shared type.
 *
 * ## How much smaller this file is than it was
 *
 * It opened much larger. While this console was being built the contract track shipped most of
 * what was provisional — the full `phone` on the admin user row, the five-state account
 * vocabulary and `POST /admin/users/:id/state`, the KYC document metadata and its own audited
 * bytes route, `POST /admin/users/:id/balance`, the bill-service icon upload and `iconPath`,
 * and the whole admin remember-me lineage. All of that moved out of here and into
 * `./types.js` and `./routes.js` as it landed, which is exactly the lifecycle this file
 * exists to have. What is left is what genuinely has no contract behind it.
 *
 * ## Three rules that keep this from becoming a second contract
 *
 *   1. **Nothing here goes into `./routes.ts`.** That file is a verbatim mirror of the real
 *      `ROUTES.admin` and `routes.test.ts` pins it against the contract by source path, so a
 *      provisional path added there fails the build. Provisional paths live here.
 *   2. **Every added field is optional.** A server that has not shipped the change simply does
 *      not send it, and the console must degrade to a stated "not available yet" rather than
 *      crash or, far worse, present a mask as if it were the whole value. The readers at the
 *      bottom are how that promise is kept at runtime rather than only at compile time.
 *   3. **Nothing here unmasks anything.** Where the API sends a mask, it stays a mask. The
 *      full-contact work is entirely about *preferring a full value the API supplies*; it
 *      never reconstructs one.
 */
import type { MoneyDTO, ProviderCost } from './types.js';

// ─────────────────────────────────────────────────────────────────────────────
// Routes
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The two paths the console asks for that the contract does not declare.
 *
 * Shaped like `ADMIN_ROUTES` so moving one across is a cut and a paste rather than a rewrite.
 * API-relative, without the `/v1` prefix, because the transport adds it.
 */
export const PENDING_ROUTES = {
  /**
   * Item 29. Editing a bill service's mark, colour and availability.
   *
   * The contract shipped the list (`ROUTES.admin.billServices`) and the icon upload
   * (`ROUTES.admin.billServiceIcon`) but no per-service write, so the letter mark and the hex
   * colour behind it are still seed-only. They matter precisely because they are the fallback:
   * every service that has no uploaded icon is drawn from them, which today is all of them.
   */
  billService: (id: string) => `/admin/bills/services/${encodeURIComponent(id)}`,

  /**
   * **DISPOSABLE.** The Busha sandbox probe, behind Diagnostics.
   *
   * Exists to settle two questions the vendor's documentation does not answer, both of which
   * the adapter being written right now is currently guessing at. See
   * `pages/BushaProbePage.tsx` for what they are and when this can go. Removable in one
   * commit, along with the page, the client method and the types below.
   */
  bushaProbe: '/admin/diagnostics/busha-probe',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Retired: items 3, 17, 31 and 33 have landed in the contract
// ─────────────────────────────────────────────────────────────────────────────
//
// Four shims lived here and all four are gone, which is exactly the lifecycle this file exists
// to have. Anything that needs these shapes imports the contract's own through `./types.js`,
// like every other shared type, and `GET /admin/users/:id/photo` is mirrored in `./routes.ts`
// with the rest of the real table.
//
//   - `PendingAvatarField` — `avatar` is published on `adminUserRowSchema`, which
//     `adminUserDetailSchema` extends, so both admin read models carry it. A path and a version
//     stamp, never bytes: the console fetches the image through the admin route above with its
//     own bearer token, because a customer's face is not a public asset the way a bank logo is.
//
//   - `PendingFullPhoneField` — `phone` is now published on `adminTxRowSchema` and
//     `adminThreadRowSchema`, joining `adminUserRowSchema`, which had it already. An operator
//     working a stuck payout off the transactions list no longer has to open the customer
//     record for a number they are allowed to see.
//
//   - `PendingProviderCost` / the `providerCost` field — published as `providerCostSchema` on
//     the transaction row. Note the contract's own point about `vendor`: it is the party that
//     *billed*, which is not always the row's `provider`, because a payout can route through
//     one rail and be billed by another after a failover.
//
//   - `PendingDocumentNumberField` — `documentNumber` is published on `adminKycDetailSchema`,
//     and it did NOT land in the shape this file asked for. **It is masked** (`*******4291`),
//     deliberately, and the contract carries a long argument for why: a BVN is a lifetime
//     identifier that unlocks every bank account a Nigerian holds, it cannot be rotated after
//     a leak, and returning it whole to a console would put it in browser memory, in an HTTP
//     cache, in any logging proxy in front of the console, and in the screenshot somebody
//     pastes into a support chat. The review question — does the document in front of me match
//     the record — is answered by the audited document image against the length and last four.
//     The console renders what it is given and does not annotate it as incomplete, because the
//     masking here is the answer rather than a gap waiting to be filled.

// ─────────────────────────────────────────────────────────────────────────────
// Item 29: editing the tile behind the icon
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A sparse patch of one bill service. An absent field is left alone, so flipping availability
 * sends `available` and nothing else.
 *
 * Bounds mirror `billServiceSchema`: the mark is one to six characters, the colour is
 * `#RRGGBB`. The API is the enforcement authority; the console checks first only to spare a
 * round-trip.
 */
export interface PendingUpdateBillServiceRequest {
  readonly mark?: string;
  readonly color?: string;
  readonly available?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// DISPOSABLE: the Busha sandbox probe
// ─────────────────────────────────────────────────────────────────────────────
//
// Everything from here to the readers is temporary and goes in one commit once the two
// questions in `pages/BushaProbePage.tsx` are answered. It is in this file rather than in
// `types.ts` because there is no contract behind it and there is not going to be one: this
// is a throwaway diagnostic, not a surface anybody builds on.

/**
 * What the console sends.
 *
 * The key travels in the **body** and nowhere else. Not a query string, not a header the
 * console sets, not a path segment: those are the three places a secret ends up in an access
 * log, a browser history entry or a referrer, and none of them is undone by deleting the page
 * afterwards.
 */
export interface PendingBushaProbeRequest {
  /**
   * Busha's bearer is base64 of the API key and the secret key together, so these travel as a
   * pair: neither authenticates anything alone, which a first live run established by
   * answering `Unauthorized request` to all twelve calls.
   */
  readonly apiKey?: string;
  readonly secretKey?: string;
  /** The encoded token, used verbatim, for when the assumed separator is wrong. */
  readonly authToken?: string;
  /** Defaults server-side to the sandbox host when absent. */
  readonly baseUrl?: string;
}


/** One call the probe made, and what came back. */
export interface PendingBushaProbeStep {
  /** `1. create customer A` — the operator reads these in order and compares 3 against 4. */
  readonly label: string;
  readonly method: string;
  readonly path: string;
  /** The vendor's HTTP status. **0 means the request never got there at all.** */
  readonly status: number;
  /** The raw response, as text. Pretty-printed by the server when it parses as JSON. */
  readonly body: string;
  /** A transport failure, when there was one. Null on any answered call, 2xx or not. */
  readonly error: string | null;
}

export interface PendingBushaProbeResponse {
  /** Echoed back so the operator can see which host was actually reached. */
  readonly baseUrl: string;
  /**
   * How the bearer was built: `derived` from the two keys, or a `pasted` encoded token. Null
   * from a server that predates the field. It belongs in the transcript because a 401 means
   * something different depending on which was used.
   */
  readonly auth: 'derived' | 'pasted' | null;
  readonly ranAt: string | null;
  readonly steps: readonly PendingBushaProbeStep[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Defensive readers
// ─────────────────────────────────────────────────────────────────────────────
//
// Everything above is optional by construction, but "optional" in TypeScript is a promise
// about a build, not about a response. These are the runtime half: each takes whatever the
// server actually sent and returns either a value the console can trust or `null`, which every
// call site renders as a stated absence rather than as a blank, a crash, or — the failure that
// matters most here — a mask presented as a whole number.

/**
 * Read a probe response, keeping every step that is legible and dropping only what is not.
 *
 * Deliberately forgiving, because the alternative fails in the one way this page must not: the
 * probe makes seven calls to a vendor nobody here has integrated yet, several of which are
 * *expected* to be refusals, and a reader that rejects the whole response over one odd step
 * would throw away the six answers the operator is waiting for. So a step with an unreadable
 * shape is skipped and the rest are rendered.
 *
 * It also accepts `steps` under either name and a `body` that arrived as an object rather than
 * as text, because the endpoint is being written concurrently with this page and neither side
 * has seen the other's code.
 */
export function readBushaProbe(value: unknown): PendingBushaProbeResponse | null {
  if (!isRecord(value)) return null;
  const rawSteps = Array.isArray(value['steps'])
    ? value['steps']
    : Array.isArray(value['results'])
      ? value['results']
      : null;
  if (rawSteps === null) return null;

  const steps: PendingBushaProbeStep[] = [];
  for (const entry of rawSteps) {
    const step = readBushaProbeStep(entry);
    if (step !== null) steps.push(step);
  }

  const auth = readText(value['auth']);
  return {
    baseUrl: readText(value['baseUrl']) ?? readText(value['base_url']) ?? '',
    // Anything the console does not recognise reads as absent rather than as a guess.
    auth: auth === 'derived' || auth === 'pasted' ? auth : null,
    ranAt: readText(value['ranAt']) ?? readText(value['ran_at']),
    steps,
  };
}


function readBushaProbeStep(value: unknown): PendingBushaProbeStep | null {
  if (!isRecord(value)) return null;
  const label = readText(value['label']) ?? readText(value['name']);
  if (label === null) return null;

  const rawStatus = value['status'];
  const status =
    typeof rawStatus === 'number' && Number.isFinite(rawStatus)
      ? Math.trunc(rawStatus)
      : typeof rawStatus === 'string' && /^\d+$/.test(rawStatus.trim())
        ? Number.parseInt(rawStatus.trim(), 10)
        : 0;

  const rawBody = value['body'] ?? value['response'];
  const body =
    typeof rawBody === 'string'
      ? rawBody
      : rawBody === undefined || rawBody === null
        ? ''
        : safeStringify(rawBody);

  return {
    label,
    method: readText(value['method']) ?? '',
    path: readText(value['path']) ?? '',
    status,
    body,
    // `skipped` is what the endpoint calls it: a step it deliberately did not run because an
    // earlier one gave it nothing to run against. Same meaning to a reader, either name.
    error: readText(value['error']) ?? readText(value['skipped']),
  };
}

/** JSON that cannot throw. A cycle in a diagnostic payload must not blank the page. */
function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2) ?? '';
  } catch {
    return String(value);
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** A non-empty string, or null. Trimmed, because a server sending `" "` has said nothing. */
export function readText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * A `MoneyDTO`, or null.
 *
 * `amount` must be a string of digits, optionally signed. A JSON number here is a double, and
 * accepting one would put a float on the money path through the back door.
 */
export function readMoney(value: unknown): MoneyDTO | null {
  if (!isRecord(value)) return null;
  const currency = readText(value['currency']);
  const amount = value['amount'];
  if (currency === null || typeof amount !== 'string') return null;
  if (!/^-?\d+$/.test(amount.trim())) return null;
  // The currency is asserted rather than narrowed. `MoneyDTO['currency']` is a closed union and
  // this reader's whole job is to survive a server that is ahead of the console: a coin added
  // upstream would otherwise be dropped by its own type guard, and a dropped amount is worse
  // than an unfamiliar currency code rendered as itself.
  return { currency: currency as MoneyDTO['currency'], amount: amount.trim() };
}

/**
 * A vendor cost, or null. A cost with no vendor attached is not attributable, so not shown.
 *
 * Kept even though `providerCost` is now a published, typed field: the console does not parse
 * responses through zod, so "typed" here is a promise about a build rather than about a body.
 * A deployment mid-rollout, a proxy that strips an unknown key, or a cost captured with no
 * vendor recorded all reach the transactions page as something the column has to survive, and
 * the alternative to this reader is `NaN` in a money column.
 */
export function readProviderCost(value: unknown): ProviderCost | null {
  if (!isRecord(value)) return null;
  const amount = readMoney(value['amount']);
  const vendor = readText(value['vendor']);
  if (amount === null || vendor === null) return null;
  const capturedAt = readText(value['capturedAt']);
  return capturedAt === null ? { amount, vendor } : { amount, vendor, capturedAt };
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-capability activation
// ─────────────────────────────────────────────────────────────────────────────
//
// `providerStatusSchema` carries **one** `enabled` switch per vendor, which was the right
// shape while every vendor served one or two capabilities that stood or fell together. It
// stops being the right shape the moment a vendor serves five: crypto custody, naira
// collection, naira payouts, the price feed and firm quotes then sit behind a single boolean,
// so an operator who wants to try that vendor's custody has to switch on its naira rails in
// the same act.
//
// Routing already covers half of it, and it is worth being precise about which half.
// `provider_configs` ranks payout, collection and biller, and an unranked vendor is never
// called — so those three genuinely are controllable per capability today, from the routing
// screen. Custody, rates and liquidity have no roster to be absent from. For those, `enabled`
// is the only control there is and it is all or nothing.
//
// What the console needs is one switch per capability held server-side beside `enabled`, and a
// `live` per capability so the page can keep the promise it already makes everywhere else: that
// a switch which changes nothing says so. Every field is optional and every reader below
// degrades to the single switch, so a console build ahead of the API renders what it renders
// today.

/** One capability of one vendor: what the operator asked for, and what is actually happening. */
export interface PendingProviderServiceState {
  readonly service: string;
  /** The operator's switch for this capability alone. */
  readonly enabled: boolean;
  /** `enabled` and every required credential resolving. Anything else is never called. */
  readonly live: boolean;
}

/**
 * Read the per-capability switches, or `null` when this API does not publish them.
 *
 * `null` and an empty array are different answers and the page treats them differently. Null
 * means "this API has one switch per vendor", and the page draws exactly the control it always
 * drew. An empty array from an API that *does* publish the field would describe a vendor with
 * no capabilities, which is not a thing, so it reads as null too. Only a non-empty, fully
 * legible array turns the per-capability controls on — a half-parsed one would render some of a
 * vendor's rails as switchable and silently drop the rest, which is worse than none.
 */
export function readProviderServiceStates(
  value: unknown,
): readonly PendingProviderServiceState[] | null {
  if (!isRecord(value)) return null;
  const raw = value['serviceStates'];
  if (!Array.isArray(raw) || raw.length === 0) return null;

  const states: PendingProviderServiceState[] = [];
  for (const entry of raw) {
    if (!isRecord(entry)) return null;
    const service = readText(entry['service']);
    if (service === null || typeof entry['enabled'] !== 'boolean') return null;
    states.push({
      service,
      enabled: entry['enabled'],
      // A build that sends the switch but not the resolved state is treated as saying nothing
      // about liveness rather than as saying "live": the whole point of the second field is to
      // catch a switch that changes nothing, and inventing it would defeat that.
      live: entry['live'] === true,
    });
  }
  return states;
}

/**
 * The per-capability half of a save. A **sparse patch**, exactly like `values`.
 *
 * A capability the operator did not touch must be absent so it keeps whatever is stored. That
 * is not a nicety here: this page saves credentials and switches in one request, so sending the
 * whole map would let an unrelated credential edit re-assert a capability state somebody else
 * changed between the page loading and Save being pressed.
 */
export interface PendingSetProviderServicesRequest {
  readonly services?: Readonly<Record<string, boolean>>;
}

// ─────────────────────────────────────────────────────────────────────────────
// What a custody vendor does to the shape of the customer app
// ─────────────────────────────────────────────────────────────────────────────
//
// Some crypto vendors will not open a customer record until that customer has been identity
// verified, and until then they issue **no deposit address and no naira account**. That is not
// a nuance of a vendor integration; it changes what the mobile app is. With such a vendor live,
// verification stops being something a customer is nudged towards and becomes the gate in front
// of holding or receiving anything at all, and the app draws entirely different screens for it.
//
// An operator moving that switch is therefore not choosing a rail, they are choosing a product
// shape, and the console has to say so **before** the switch moves rather than after support
// starts taking calls. Everything below exists to put one sentence next to that control.
//
// The contract change asked for is one boolean on the provider status, published by the same
// catalogue that already knows every other fact about a vendor:
//
//     requiresCustomerVerification: boolean
//
// Until it lands, {@link custodyRequiresVerification} prefers the published field and falls back
// to the list below.

/**
 * Vendors known to refuse an unverified customer, by catalogue slug.
 *
 * **A fallback, and a deliberately poor one.** It is a fact about a vendor's product that this
 * console cannot discover and has no business asserting: it is here because the alternative is
 * an operator flipping a switch that silently changes what every customer's wallet can do, with
 * nothing on screen about it. A hard-coded list is a bad way to know something and it is better
 * than not knowing it.
 *
 * It is wrong in exactly one direction on purpose. A vendor missing from it produces no warning,
 * which is the state the console is in today. A vendor wrongly on it would warn about a shape
 * change that does not happen, which is a false alarm about customers' money and would teach
 * operators to disregard the notice. So nothing goes on this list without having been observed
 * refusing an unverified customer against the live API.
 */
const VENDORS_REQUIRING_CUSTOMER_VERIFICATION: readonly string[] = ['busha'];

/**
 * Does this vendor's crypto custody require the customer to be verified first?
 *
 * The published field wins wherever it exists, so the day the contract lands this reads the
 * server's answer and the list above becomes dead weight to be deleted with the rest of this
 * file. Only a real boolean counts: a field present and not a boolean is a shape this build
 * does not understand, and guessing at it is how a console starts describing something nobody
 * implemented.
 */
export function custodyRequiresVerification(provider: unknown): boolean {
  if (!isRecord(provider)) return false;
  const published = provider['requiresCustomerVerification'];
  if (typeof published === 'boolean') return published;
  const slug = readText(provider['slug']);
  return slug !== null && VENDORS_REQUIRING_CUSTOMER_VERIFICATION.includes(slug);
}

// ─────────────────────────────────────────────────────────────────────────────
// Customer identity verification, as an operator sees it
// ─────────────────────────────────────────────────────────────────────────────
//
// `adminUserDetailSchema` publishes `kycStatus` and `tier`, which says *what* happened and
// nothing about *how* or *when*. Where the check runs on a page this platform does not host,
// that is the operator's only window into it: there is no document to open and nothing in the
// review queue, and the call they are on is a customer saying they were refused. The three
// facts that answer that call are which route the check took, when it was decided, and the
// safe-to-show reason.
//
// Never the document value, and never the partner's own identifier for the customer. Neither is
// something an operator acts on, and both are things that get read aloud once they are on a
// screen.

export interface PendingIdentityVerification {
  /**
   * Which route the verdict came by.
   *
   * `hosted` — completed on a page this platform does not host. `documents` — a BVN or NIN this
   * platform collected and matched itself. `manual` — a person here decided it. Null when the
   * API knows the status but not its provenance, which is every record written before this
   * field existed.
   */
  readonly source: 'hosted' | 'documents' | 'manual' | null;
  readonly submittedAt: string | null;
  readonly decidedAt: string | null;
  /** Safe to render verbatim. Never contains a document value. */
  readonly reason: string | null;
}

/** Read it, or `null` when this API does not publish it. Every field independently optional. */
export function readIdentityVerification(value: unknown): PendingIdentityVerification | null {
  if (!isRecord(value)) return null;
  const raw = value['identityVerification'];
  if (!isRecord(raw)) return null;

  const source = readText(raw['source']);
  return {
    source: source === 'hosted' || source === 'documents' || source === 'manual' ? source : null,
    submittedAt: readText(raw['submittedAt']),
    decidedAt: readText(raw['decidedAt']),
    reason: readText(raw['reason']),
  };
}
